import type { TFunction } from 'i18next';
import { ArrowLeftIcon } from 'lucide-react';
import { Navigate, useParams } from 'react-router-dom';

import { EXAMPLE_GROUP_ID } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type { RecurringRuleDto } from '@splitfiesta/shared';
import { Alert } from '@splitfiesta/ui';

import { withAuth } from '@/components/auth';
import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { TabContentLoader } from '@/components/common/TabContentLoader';
import { RecurringRulesList } from '@/components/groups/RecurringRulesList';
import { AppLayout } from '@/components/layout/AppLayout';

import { useFeatureFlag } from '@/hooks/queries/useFeatureFlagsQuery';
import { useGroupQuery } from '@/hooks/queries/useGroups';
import {
  useRecurringRulesQuery,
  useStopRecurringRuleMutation,
  useUpdateRecurringRuleMutation,
} from '@/hooks/queries/useRecurringRules';

import { useAuthStore } from '@/stores/authStore';

import { handleApiError } from '@/utils/errorUtils';
import { toast } from '@/utils/toast';

function GroupRecurringRulesPage() {
  const { id: groupId = '' } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const groupsLoaded = useNamespace('groups');
  const expensesLoaded = useNamespace('expenses');
  const errorsLoaded = useNamespace('errors');
  const proEnabled = useFeatureFlag('pro');
  const available = proEnabled && groupId !== EXAMPLE_GROUP_ID;

  const { data: group } = useGroupQuery(groupId, available);
  const userId = useAuthStore(state => state.user?.id);
  const rulesQuery = useRecurringRulesQuery(groupId, available);
  const updateRule = useUpdateRecurringRuleMutation(groupId);
  const stopRule = useStopRecurringRuleMutation(groupId);

  if (!groupId) return <Navigate to="/groups" replace />;
  if (!groupsLoaded || !expensesLoaded || !errorsLoaded) return null;

  const onError = (error: unknown) =>
    toast.error(handleApiError(error, t as TFunction, 'errors'));

  const setStatus = (rule: RecurringRuleDto, status: 'active' | 'paused') =>
    updateRule.mutate({ ruleId: rule.id, status }, { onError });

  const pendingRuleId = updateRule.isPending
    ? (updateRule.variables?.ruleId ?? null)
    : stopRule.isPending
      ? (stopRule.variables ?? null)
      : null;

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center mb-8">
          <RouteChangeLink
            href={`/groups/${groupId}`}
            aria-label={t('common:actions.back')}
            className="mr-4 flex min-h-11 min-w-11 items-center justify-center rounded-lg hover:bg-[#FAF9F7] dark:hover:bg-gray-800/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
          >
            <ArrowLeftIcon
              aria-hidden="true"
              className="w-5 h-5 text-gray-600 dark:text-gray-400"
            />
          </RouteChangeLink>
          <div className="min-w-0">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('groups:recurring.title')}
            </h1>
            {group?.name && (
              <p className="mt-1 break-words text-sm text-gray-500 dark:text-gray-400">
                {group.name}
              </p>
            )}
          </div>
        </div>

        {!available ? (
          <Alert type="error" message={t('groups:errors.groupNotFound')} />
        ) : rulesQuery.error ? (
          <Alert type="error" message={t('groups:errors.loadFailed')} />
        ) : !rulesQuery.data ? (
          <TabContentLoader />
        ) : (
          <RecurringRulesList
            rules={rulesQuery.data}
            viewer={
              userId && group?.userRole
                ? { id: userId, role: group.userRole }
                : null
            }
            pendingRuleId={pendingRuleId}
            onPause={rule => setStatus(rule, 'paused')}
            onResume={rule => setStatus(rule, 'active')}
            onStop={rule => stopRule.mutate(rule.id, { onError })}
          />
        )}
      </div>
    </AppLayout>
  );
}

export default withAuth(GroupRecurringRulesPage);
